import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AMENITIES } from "@/lib/i18n";
import { Upload, X, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", hotel_name: "", city: "", country: "Italy", address: "", description: "", price_per_day: 25, max_guests: 20, amenities: [], photos: [] };
const MAX_PHOTOS = 10;

export default function CreateListing() {
  const { t, user, loading } = useApp();
  const nav = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const editing = !!id;

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "owner") { nav("/auth"); return; }
    if (id) api.get(`/pools/${id}`).then((r) => setForm({ ...r.data, photos: r.data.photos || [] }));
    else if (user.hotel_name) setForm((f) => ({ ...f, hotel_name: user.hotel_name }));
  }, [id, user, loading, nav]);

  const toggleAmenity = (a) => setForm((f) => ({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] }));

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_PHOTOS - form.photos.length;
    if (remaining <= 0) return toast.error(`Max ${MAX_PHOTOS} photos`);
    for (const file of files.slice(0, remaining)) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, data.path] }));
      } catch { toast.error("Upload failed"); }
    }
    e.target.value = "";
  };

  const removePhoto = (p) => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }));

  const move = (idx, delta) => {
    setForm((f) => {
      const arr = [...f.photos];
      const j = idx + delta;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, photos: arr };
    });
  };

  const setAsCover = (idx) => {
    setForm((f) => {
      if (idx === 0) return f;
      const arr = [...f.photos];
      const [pick] = arr.splice(idx, 1);
      arr.unshift(pick);
      return { ...f, photos: arr };
    });
    toast.success(t("gallery.coverSet"));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, price_per_day: +form.price_per_day, max_guests: +form.max_guests };
      if (editing) await api.patch(`/pools/${id}`, payload);
      else await api.post("/pools", payload);
      toast.success("Saved!");
      nav("/owner");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="font-display text-4xl font-bold mb-8">{editing ? t("create.edit") : t("create.title")}</h1>
      <form onSubmit={save} className="space-y-6 bg-white border border-border rounded-2xl p-8">
        <div>
          <Label>{t("create.titleField")}</Label>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="input-title" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>{t("create.hotelName")}</Label><Input required value={form.hotel_name} onChange={(e) => setForm({ ...form, hotel_name: e.target.value })} data-testid="input-hotel" /></div>
          <div><Label>{t("create.city")}</Label><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="input-city" /></div>
          <div><Label>{t("create.country")}</Label><Input required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="input-country" /></div>
          <div><Label>{t("create.address")}</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="input-address" /></div>
        </div>
        <div>
          <Label>{t("create.description")}</Label>
          <Textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="input-description" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>{t("create.price")}</Label><Input required type="number" min="1" step="0.5" value={form.price_per_day} onChange={(e) => setForm({ ...form, price_per_day: e.target.value })} data-testid="input-price" /></div>
          <div><Label>{t("create.maxGuests")}</Label><Input required type="number" min="1" value={form.max_guests} onChange={(e) => setForm({ ...form, max_guests: e.target.value })} data-testid="input-maxguests" /></div>
        </div>
        <div>
          <Label className="mb-2 block">{t("create.amenities")}</Label>
          <div className="flex flex-wrap gap-2">
            {AMENITIES.map((a) => (
              <button type="button" key={a} onClick={() => toggleAmenity(a)} data-testid={`create-amenity-${a}`}
                className={`text-sm px-4 py-2 rounded-full border transition ${form.amenities.includes(a) ? "bg-[#005C7A] text-white border-[#005C7A]" : "border-border bg-white hover:border-[#005C7A]"}`}>
                {t(`amenity.${a}`)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>{t("gallery.title")} · {form.photos.length}/{MAX_PHOTOS}</Label>
            <span className="text-xs text-muted-foreground">{t("gallery.help")}</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-3">
            {form.photos.map((p, i) => (
              <div key={p} className={`relative aspect-square rounded-lg overflow-hidden bg-muted group ${i === 0 ? "ring-2 ring-[#E27B58]" : ""}`} data-testid={`photo-${i}`}>
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <div className="absolute top-1 left-1 bg-[#E27B58] text-white text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-white" />{t("gallery.cover")}</div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex flex-col justify-between p-1">
                  <div className="flex justify-end">
                    <button type="button" onClick={() => removePhoto(p)} className="bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition" data-testid={`remove-photo-${i}`}><X className="w-3 h-3" /></button>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="flex-1 bg-white/95 text-[#1A202C] rounded p-1 disabled:opacity-30" data-testid={`move-left-${i}`}><ArrowLeft className="w-3 h-3 mx-auto" /></button>
                    {i !== 0 && (
                      <button type="button" onClick={() => setAsCover(i)} className="bg-[#E27B58] text-white rounded p-1 text-[10px] font-bold px-2" data-testid={`set-cover-${i}`}>{t("gallery.setCover")}</button>
                    )}
                    <button type="button" disabled={i === form.photos.length - 1} onClick={() => move(i, 1)} className="flex-1 bg-white/95 text-[#1A202C] rounded p-1 disabled:opacity-30" data-testid={`move-right-${i}`}><ArrowRight className="w-3 h-3 mx-auto" /></button>
                  </div>
                </div>
              </div>
            ))}
            {form.photos.length < MAX_PHOTOS && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-[#005C7A] transition">
                <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                <span className="text-xs text-muted-foreground text-center px-2">{t("gallery.upload")}</span>
                <input type="file" multiple accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="upload-input" />
              </label>
            )}
          </div>
          {form.photos.length === 0 && <p className="text-xs text-muted-foreground">{t("gallery.hint")}</p>}
        </div>

        <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#005C7A] hover:bg-[#004A63] h-12" data-testid="submit-listing">
          {busy ? t("create.saving") : t("create.save")}
        </Button>
      </form>
    </div>
  );
}
